<template>
  <div id="attribute-action-menu">
    <action-menu-wrapper :items="options" :offset="offset" teleport />

    <confirm-modal
      v-if="showDeleteModal"
      :title="`${$t('COMMON.DELETE')} ${$t('ATTRIBUTE_BUILDER.ATTRIBUTE')}`"
      :description="
        $t('ORGANIZATION.COMMUNICATION_TEMPLATES.DELETE_DESCRIPTION', {
          name: model?.name || $t('ATTRIBUTE_BUILDER.ATTRIBUTE'),
        })
      "
      :confirm-button="$t('COMMON.DELETE')"
      confirm-button-type="error"
      :close="() => (showDeleteModal = false)"
      @confirm="deleteAttribute"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { CustomAttributeStatusOption } from "@/enums/customAttributes";
import { getAsyncComponent } from "@/hooks/common";

import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuWrapper, {
  type MenuItem,
} from "@/components/ui/ActionMenuWrapperV2.vue";

import type { CustomAttribute } from "@/models/customAttributes/customAttribute";
import type { PropType } from "vue";

const IconEdit = getAsyncComponent(
  () => import("@/components/icons/IconEdit.vue")
);
const UploadFileMinimal = getAsyncComponent(
  () => import("@/components/icons/deals/IconUploadFileMinimal.vue")
);
const IconDraftDoc = getAsyncComponent(
  () => import("@/components/icons/IconDraftDoc.vue")
);
const IconDelete = getAsyncComponent(
  () => import("@/components/icons/IconDelete.vue")
);

const emit = defineEmits<{
  "attribute:save": [CustomAttributeStatusOption];
  "attribute:edit": [CustomAttribute["id"]];
  "attribute:delete": [CustomAttribute["id"]];
}>();

const props = defineProps({
  model: {
    type: Object as PropType<CustomAttribute>,
  },
  teleport: {
    type: Boolean,
    default: true,
  },
  actions: {
    type: Object as PropType<{
      publish?: boolean;
      draft?: boolean;
      delete?: boolean;
    }>,
    default: () => ({ delete: true }),
  },
});

const offset = { left: -60, top: 5 };

const { t } = useI18n();

const options = computed(() => {
  const menuItems: MenuItem[] = [];
  if (props.actions.delete) {
    menuItems.unshift({
      label: t("COMMON.DELETE"),
      icon: IconDelete,
      handler: () => (showDeleteModal.value = true),
    });
  }
  if (props.actions.draft) {
    menuItems.unshift({
      label: t("COMMON.DRAFT"),
      icon: IconDraftDoc,
      color: "none",
      handler: () => emit("attribute:save", CustomAttributeStatusOption.draft),
    });
  }
  if (props.actions.publish) {
    menuItems.unshift({
      label: t("COMMON.PUBLISH"),
      icon: UploadFileMinimal,
      handler: () =>
        emit("attribute:save", CustomAttributeStatusOption.published),
    });
  }
  if (props.model?.is_legacy) {
    menuItems.unshift({
      label: t("COMMON.EDIT"),
      icon: IconEdit,
      handler: edit,
    });
  }
  return menuItems;
});

const showDeleteModal = ref(false);

function edit() {
  if (props.model) {
    emit("attribute:edit", props.model?.id);
  }
}

const deleteAttribute = () => {
  if (props.model) {
    emit("attribute:delete", props.model?.id);
  }
};
</script>
